import { getState, num } from '../../helpers';
import { updateChartProps } from './refresh';
function isChartAppRoot(chartNode) {
    var app = chartNode.getApp();
    if (!app)
        return false;
    return app.stage.children.length === 1 && app.stage.children[0] === chartNode;
}
/** Keep the LightDraw app viewport aligned with chart container bounds (gallery / maximize). */
function syncAppViewport(chartNode, width, height) {
    var app = chartNode.getApp();
    if (!app || !isChartAppRoot(chartNode))
        return;
    var size = app.getSize();
    if (size.width !== width || size.height !== height) {
        app.resize(width, height);
    }
}
var observers = new WeakMap();
function createResizeObserver(callback) {
    if (typeof ResizeObserver !== 'undefined') {
        return new ResizeObserver(callback);
    }
    return {
        observe: function () { },
        unobserve: function () { },
        disconnect: function () { },
    };
}
/** Keep chart props in sync when a container element is resized. */
export function installChartResizeObserver(chartNode, container, options) {
    var _a, _b, _c;
    if (options === void 0) { options = {}; }
    detachChartResizeObserver(chartNode);
    var minW = (_a = options.minWidth) !== null && _a !== void 0 ? _a : 64;
    var minH = (_b = options.minHeight) !== null && _b !== void 0 ? _b : 48;
    var pad = (_c = options.padding) !== null && _c !== void 0 ? _c : 0;
    var watchHeight = options.watchHeight !== false;
    var syncSize = options.syncSize !== false;
    var raf = 0;
    var ro = createResizeObserver(function (entries) {
        var _a;
        var rect = (_a = entries[0]) === null || _a === void 0 ? void 0 : _a.contentRect;
        if (!rect)
            return;
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(function () {
            applyChartContainerSize(chartNode, rect.width, rect.height, {
                minW: minW,
                minH: minH,
                pad: pad,
                watchHeight: watchHeight,
                syncSize: syncSize,
            });
        });
    });
    ro.observe(container);
    observers.set(chartNode, ro);
    chartNode.metadata.resizeObserverAttached = true;
    applyChartContainerSize(chartNode, container.clientWidth, container.clientHeight, {
        minW: minW,
        minH: minH,
        pad: pad,
        watchHeight: watchHeight,
        syncSize: syncSize,
    });
}
export function detachChartResizeObserver(chartNode) {
    var ro = observers.get(chartNode);
    ro === null || ro === void 0 ? void 0 : ro.disconnect();
    observers.delete(chartNode);
    delete chartNode.metadata.resizeObserverAttached;
}
function applyChartContainerSize(chartNode, rawW, rawH, opts) {
    var w = Math.max(opts.minW, Math.floor(rawW - opts.pad));
    var h = Math.max(opts.minH, Math.floor(rawH - opts.pad));
    var last = chartNode.metadata._lastContainerSize;
    if (last && last.w === w && last.h === h) {
        syncAppViewport(chartNode, w, h);
        return;
    }
    syncAppViewport(chartNode, w, h);
    var state = getState(chartNode);
    var patch = {};
    var hasWidth = num(state, 'width', 0) > 0 || 'width' in state;
    var hasHeight = num(state, 'height', 0) > 0 || 'height' in state;
    var hasSize = num(state, 'size', 0) > 0;
    if (hasWidth && w > 0 && w !== num(state, 'width', 0))
        patch.width = w;
    if (opts.watchHeight && hasHeight && h > 0 && h !== num(state, 'height', 0))
        patch.height = h;
    if (opts.syncSize && hasSize && !hasWidth) {
        var sz = opts.watchHeight ? Math.min(w, h) : w;
        if (sz > 0 && sz !== num(state, 'size', 0))
            patch.size = sz;
    }
    else if (opts.syncSize && hasSize && hasWidth && opts.watchHeight) {
        var sz = Math.min(w, h);
        if (sz > 0 && sz !== num(state, 'size', 0))
            patch.size = sz;
    }
    if (Object.keys(patch).length > 0) {
        chartNode.metadata._lastContainerSize = { w: w, h: h };
        updateChartProps(chartNode, patch);
    }
    else {
        chartNode.metadata._lastContainerSize = { w: w, h: h };
    }
}
